import { useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";

import { useCreateIssueInviteLink } from "@/hooks/use-issue-mutations";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ApiError, OfflineError } from "@/lib/api";
import { issueShareText } from "@/lib/issues";
import { shareLink, type ShareOutcome } from "@/lib/share";
import type { ProjectIssue } from "@/types/api";

function linkErrorMessage(err: Error): string {
  if (err instanceof OfflineError) return "You're offline. Connect to send a link.";
  if (err instanceof ApiError && err.status === 403) {
    return "You can't add people to this issue any more.";
  }
  return "Couldn't make a link. Try again.";
}

/**
 * Send a link: make a fresh one, then hand it to the phone's share sheet, or
 * copy it on a desktop. See [[add-contributor-sheet.spec]] § Behavior.
 *
 * Browsers only share, or write to the clipboard, close to the tap that asked,
 * and making the link is a request first. On a slow site connection an iPhone
 * refuses by the time the link arrives. Then the link is kept in `readyUrl`
 * for the host's "Your link is ready" dialog, whose button is a fresh tap.
 */
export function useSendIssueLink(
  projectId: string,
  issue: Pick<ProjectIssue, "id" | "number" | "title">,
) {
  const createLink = useCreateIssueInviteLink();
  // A phone's share sheet goes straight into WhatsApp; on a desktop the user
  // wants something to paste. The same test the update sheet's camera uses.
  const viaShareSheet = useMediaQuery("(pointer: coarse)");
  const [readyUrl, setReadyUrl] = useState<string | null>(null);

  const deliver = async (url: string): Promise<ShareOutcome> => {
    const outcome = await shareLink({
      url,
      text: issueShareText(issue),
      viaShareSheet,
    });
    if (outcome === "copied") toast("Link copied");
    return outcome;
  };

  /**
   * Make a link and send it. Resolves `true` once a link exists — sent,
   * copied, dismissed, or waiting in the ready dialog — so the host can close;
   * `false` when none could be made, with a toast saying why.
   */
  const send = async (): Promise<boolean> => {
    if (createLink.isPending) return false;
    let url: string;
    try {
      ({ url } = await createLink.mutateAsync({ projectId, issueId: issue.id }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      toast(linkErrorMessage(error));
      if (
        !(error instanceof OfflineError) &&
        !(error instanceof ApiError && error.status < 500)
      ) {
        Sentry.captureMessage("Create issue invite link failed", {
          level: "error",
          tags: { area: "issue-contributors" },
          extra: {
            projectId,
            issueId: issue.id,
            errorName: error.name,
            status: error instanceof ApiError ? error.status : undefined,
          },
        });
      }
      return false;
    }

    if ((await deliver(url)) === "blocked") setReadyUrl(url);
    return true;
  };

  /** The ready dialog's button. Blocked again, the dialog stays with the link. */
  const retry = async () => {
    if (!readyUrl) return;
    if ((await deliver(readyUrl)) !== "blocked") setReadyUrl(null);
  };

  return {
    send,
    sending: createLink.isPending,
    readyUrl,
    retry,
    dismissReady: () => setReadyUrl(null),
    viaShareSheet,
  };
}
